// src/App.js
import React,{ useCallback,useEffect,useState } from 'react';
import { Navigate,Route,BrowserRouter as Router,Routes } from 'react-router-dom';
import { ToastContainer,toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Components
import Header from './components/Header';
import { FaExclamationTriangle } from 'react-icons/fa';

import AutomationEditor from './components/automations/AutomationEditor';
import AutomationList from './components/automations/AutomationList';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import ConversationPanel from './components/conversation/ConversationPanel';
import DeviceManager from './components/devices/DeviceManager';
import SmartLockManager from './components/devices/SmartLockManager';
import GlobalAudioWrapper from './components/microphone/GlobalAudioWrapper';
import MicrophoneList from './components/microphone/MicrophoneList';
import SceneControl from './components/scenes/SceneControl';
import SettingsPanel from './components/settings/SettingsPanel';
import LoginForm from './components/auth/LoginForm';

// Feature-aware component wrapper
import FeatureRoute from './components/common/FeatureRoute';

// Context
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { FeatureProvider, useFeatures } from './context/FeatureContext';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';

// Conditional Global Audio Wrapper Component
const ConditionalGlobalAudioWrapper = () => {
  const { isFeatureEnabled } = useFeatures();
  
  if (!isFeatureEnabled('microphones')) {
    return null;
  }
  
  return <GlobalAudioWrapper />;
};

// Main App Content Component that waits for feature loading
const AppContent = ({ systemStatus }) => {
  const { loading: featuresLoading } = useFeatures();
  
  // Show loading spinner while features are being loaded
  if (featuresLoading) {
    return (
      <main className="app-content flex items-center justify-center">
        <LoadingSpinner size="large" />
        <h2 className="ml-4">Loading features...</h2>
      </main>
    );
  }
  
  return (
    <main className="app-content">
      {!systemStatus.online && (
        <div className="connection-error-banner">
          <FaExclamationTriangle />
          <span>Connection to the smart home system has been lost. Attempting to reconnect...</span>
        </div>
      )}
      
      <Routes>
        <Route path="/" element={<Navigate to="/settings" />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/microphones" element={
          <FeatureRoute feature="microphones" showDisabledMessage>
            <MicrophoneList />
          </FeatureRoute>
        } />
        <Route path="/devices" element={
          <FeatureRoute feature="devices" showDisabledMessage>
            <DeviceManager />
          </FeatureRoute>
        } />

        <Route path="/smartlocks" element={
          <FeatureRoute feature="smartLocks" showDisabledMessage>
            <SmartLockManager />
          </FeatureRoute>
        } />
        <Route path="/scenes" element={
          <FeatureRoute feature="scenes" showDisabledMessage>
            <SceneControl />
          </FeatureRoute>
        } />
        <Route path="/conversation" element={
          <FeatureRoute feature="conversations" showDisabledMessage>
            <ConversationPanel />
          </FeatureRoute>
        } />
        <Route path="/automations" element={
          <FeatureRoute feature="automations" showDisabledMessage>
            <AutomationList />
          </FeatureRoute>
        } />
        <Route path="/automations/:automationId" element={
          <FeatureRoute feature="automations" showDisabledMessage>
            <AutomationEditor />
          </FeatureRoute>
        } />
        <Route path="/settings" element={<SettingsPanel />} />
      </Routes>
    </main>
  );
};

const API_BASE_URL = '';
const API_PATH = '/api';


function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState({
    online: false,
    latency: null,
    lastUpdate: null
  });
  
  // Check system status
  const checkSystemStatus = useCallback(async () => {
    try {
      const startTime = Date.now();
      const response = await fetch(`${API_BASE_URL}${API_PATH}/status`);
      const latency = Date.now() - startTime;
      
      if (response.ok) {
        // console.log('data',data);
        const data = await response.json();
        setSystemStatus({
          online: true,
          latency,
          lastUpdate: new Date(),
          ...data
        });
        
        if (isLoading) setIsLoading(false);
      } else {
        throw new Error('System offline');
      }
    } catch (error) {
      setSystemStatus({
        online: false,
        latency: null,
        lastUpdate: new Date()
      });
      
      if (isLoading) {
        // Only show toast if we were previously loading (first connection)
        toast.error('Unable to connect to the smart home system. Please check your connection.');
      }
    }
  }, [isLoading]);
  
  useEffect(() => {
    // Check status immediately
    checkSystemStatus();
    
    // Set up periodic status checks
    const statusInterval = setInterval(checkSystemStatus, 30000);
    
    return () => {
      clearInterval(statusInterval);
    };
  }, [checkSystemStatus]);
  
  if (isLoading) {
    return (
      <div className="app-loading">
        <LoadingSpinner size="large" />
        <h2>Connecting to Smart Home System...</h2>
      </div>
    );
  }
  
  return (
    <AuthProvider>
      <FeatureProvider>
        <AppProvider>
          <ErrorBoundary>
          <Router>
            <ProtectedRoute>
              <div className="app-container">
                <Header systemStatus={systemStatus} />
                <AppContent systemStatus={systemStatus} />
                
                <footer className="app-footer">
                  <p>Smart Home Control Center - v1.0.0</p>
                  <span className="status-indicator">
                    <span className={`status-dot ${systemStatus.online ? 'online' : 'offline'}`}></span>
                    {systemStatus.online ? 'System Online' : 'System Offline'}
                    {systemStatus.latency && ` - Latency: ${systemStatus.latency}ms`}
                  </span>
                </footer>
                
                {/* Global Audio Player that persists across all tabs */}
                <ConditionalGlobalAudioWrapper />
                
                <ToastContainer position="bottom-right" />
              </div>
            </ProtectedRoute>
          </Router>
          </ErrorBoundary>
        </AppProvider>
      </FeatureProvider>
    </AuthProvider>
  );
}

export default App;
